import React, { useState, useContext, useEffect, ReactNode } from 'react';
import axios from 'axios';

import { formatStores, getUrlParamValue, sortStoresByDistance } from '../utils';
import {
    IStore,
    IAvailableStore,
    ILocation,
    ISearchTermLocations,
} from '../types';
import { MOBILE_BREAKPOINT_PX } from '../constants';

interface IProps {
    children: ReactNode;
}

interface IStateContext {
    availableStores?: IAvailableStore[];
    storesSortedByDistance?: IAvailableStore[];
    availableStoresError: boolean;
    centerLocation?: ILocation;
    findingCurrentLocation: boolean;
    mobileView: boolean;
    showMobileMap: boolean;
    searchLocation?: ILocation;
    searchInputValue: string;
    placeId?: string;
    searchTermLocations?: ISearchTermLocations;
    selectedStore?: IAvailableStore;
    useCurrentLocation: boolean;
    isOakley: boolean;
    isNuance: boolean;
    setAvailableStores(stores: IAvailableStore[]): void;
    setAvailableStoresError(value: boolean): void;
    setCenterLocation(location: ILocation): void;
    setFindingCurrentLocation(value: boolean): void;
    setShowMobileMap(value: boolean): void;
    setSearchLocation(location: ILocation | undefined): void;
    setSearchInputValue(value: string): void;
    setPlaceId(value: string): void;
    setSelectedStore(store: IAvailableStore | undefined): void;
    setUseCurrentLocation(value: boolean): void;
}

export const StateContext = React.createContext<IStateContext | undefined>(
    undefined
);

export const StateContextProvider = ({ children }: IProps) => {
    const buildBrand = process.env.REACT_APP_BRAND?.toLowerCase();
    const urlBrand = getUrlParamValue('brand')?.toLowerCase();
    const brand = buildBrand || urlBrand || 'ray-ban';
    const isOakley = brand === 'oakley';
    const isNuance = brand === 'nuance';
    const [availableStores, setAvailableStores] = useState<
        IAvailableStore[] | undefined
    >();
    const [storesSortedByDistance, setStoresSortedByDistance] = useState<
        IAvailableStore[] | undefined
    >();
    const [availableStoresError, setAvailableStoresError] = useState(false);
    const [centerLocation, setCenterLocation] = useState<
        ILocation | undefined
    >();
    const [findingCurrentLocation, setFindingCurrentLocation] = useState(false);
    const [showMobileMap, setShowMobileMap] = useState(false);
    const [searchLocation, setSearchLocation] = useState<
        ILocation | undefined
    >();
    const [searchInputValue, setSearchInputValue] = useState('');
    const [placeId, setPlaceId] = useState<string | undefined>(
        getUrlParamValue('placeId') || undefined
    );
    const [selectedStore, setSelectedStore] = useState<
        IAvailableStore | undefined
    >();
    const [useCurrentLocation, setUseCurrentLocation] = useState(false);
    const [searchTermLocations, setSearchTermLocations] = useState<
        ISearchTermLocations | undefined
    >();
    const [mobileView, setMobileView] = useState(
        window.innerWidth < MOBILE_BREAKPOINT_PX
    );

    const fetchAvailableStores = () => {
        axios
            .get<IStore[]>(process.env.REACT_APP_STORES_URL_PNL || '')
            .then(({ data }) => {
                const filteredStores = data.filter((store: IStore) => {
                    if (isOakley) return store.u_smd_meta_oakley === 'true';
                    if (isNuance) return store.nuance_audio === 'true';
                    return store.u_smd_meta_ray_ban === 'true';
                });
                const availableStores = formatStores(filteredStores);

                setAvailableStores(availableStores);
            })
            .catch(() => {
                setAvailableStoresError(true);
            });
    };

    const fetchSearchTermLocations = () => {
        axios
            .get<ISearchTermLocations>(
                process.env.REACT_APP_SEARCH_TERM_LOCATIONS_API || ''
            )
            .then(({ data }) => {
                setSearchTermLocations(data);
            });
    };

    useEffect(fetchAvailableStores, [isOakley, isNuance]);
    useEffect(fetchSearchTermLocations, []);

    useEffect(() => {
        const handleResize = () => {
            const isMobile = window.innerWidth < MOBILE_BREAKPOINT_PX;
            setMobileView(isMobile);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (!searchLocation || !availableStores) return;

        setStoresSortedByDistance(
            sortStoresByDistance(availableStores, searchLocation)
        );
    }, [availableStores, searchLocation]);

    return (
        <StateContext.Provider
            value={{
                availableStores,
                storesSortedByDistance,
                availableStoresError,
                centerLocation,
                findingCurrentLocation,
                mobileView,
                showMobileMap,
                searchLocation,
                searchInputValue,
                placeId,
                searchTermLocations,
                selectedStore,
                useCurrentLocation,
                isOakley,
                isNuance,
                setAvailableStores,
                setCenterLocation,
                setFindingCurrentLocation,
                setShowMobileMap,
                setSearchLocation,
                setSearchInputValue,
                setPlaceId,
                setSelectedStore,
                setUseCurrentLocation,
                setAvailableStoresError,
            }}
        >
            {children}
        </StateContext.Provider>
    );
};

export function useStateContext() {
    const context = useContext(StateContext);

    if (context === undefined) {
        throw new Error('useStateContext must be used within a provider');
    }

    return context;
}
